import type { CSSProperties } from 'react';
import type { Slide } from '@/framework/types';
import { ChartSlide, TableSlide, DiagramSlide, BrandSlide, SingleColumnSlide, DoubleColumnSlide, PeopleSlide, PeopleCard, CtaSlide, Header } from '@/framework/layouts';

/** Type-ladder rows mirroring the DS handover Typography frame. */
const TYPE_ROLES = [
  { name: 'H1 · 72', font: 'var(--sn-font-display)', size: 'var(--sn-type-h1)', weight: 500, lh: 1.06, ls: '-0.02em', upper: false, sample: 'Heading 1' },
  { name: 'H2 · 56', font: 'var(--sn-font-display)', size: 'var(--sn-text-3xl)', weight: 500, lh: 1.07, ls: '-0.02em', upper: false, sample: 'Heading 2' },
  { name: 'Subhead · 32', font: 'var(--sn-font-body)', size: 'var(--sn-type-subhead)', weight: 500, lh: 1.19, ls: '-0.01em', upper: false, sample: 'Subhead' },
  { name: 'Body M · 22', font: 'var(--sn-font-body)', size: 'var(--sn-type-body-m)', weight: 400, lh: 1.45, ls: '0', upper: false, sample: 'Body M — main body text' },
  { name: 'Body S · 20', font: 'var(--sn-font-body)', size: 'var(--sn-type-body-s)', weight: 400, lh: 1.5, ls: '0', upper: false, sample: 'Body S — dense / table text' },
  { name: 'Caption · 16', font: 'var(--sn-font-body)', size: 'var(--sn-type-caption)', weight: 400, lh: 1.5, ls: '0', upper: false, sample: 'Caption — timeline, footnotes' },
  { name: 'Label · 20', font: 'var(--sn-font-mono)', size: 'var(--sn-type-label)', weight: 400, lh: 1.1, ls: '0.04em', upper: true, sample: 'Label' },
  { name: 'Footnote · 15', font: 'var(--sn-font-mono)', size: 'var(--sn-type-footnote)', weight: 400, lh: 1.2, ls: '0.04em', upper: true, sample: 'Footnote' },
] as const;

/** Color families mirroring the DS handover Colors frame. */
const COLOR_FAMILIES = [
  { name: 'Blue', prefix: 'blue', steps: [1, 2, 3, 4, 5, 6, 7] },
  { name: 'Teal', prefix: 'teal', steps: [1, 2, 3, 4, 5, 6, 7] },
  { name: 'Beige', prefix: 'beige', steps: [1, 2, 3, 4] },
  { name: 'Grey', prefix: 'grey', steps: [0, 1, 2, 3, 4, 5, 6, 7, 8] },
  { name: 'Red', prefix: 'red', steps: [0, 1, 2, 3, 4, 5, 6, 7, 8] },
] as const;

/* ── Color Usage demo (mirrors the DS handover "Color Usage Guidelines" 3 examples) ── */
const USAGE_GLYPH = (
  <svg width="34" height="34" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <rect x="3" y="3" width="7.5" height="7.5" rx="1.6" />
    <rect x="13.5" y="3" width="7.5" height="7.5" rx="1.6" />
    <rect x="3" y="13.5" width="7.5" height="7.5" rx="1.6" />
    <rect x="13.5" y="13.5" width="7.5" height="7.5" rx="1.6" />
  </svg>
);

function UsageBlock() {
  return (
    <>
      <span style={{ color: 'var(--sn-icon)', display: 'inline-flex' }}>{USAGE_GLYPH}</span>
      <span style={{ fontFamily: 'var(--sn-font-mono)', fontSize: 'var(--sn-type-footnote)', color: 'var(--sn-eyebrow)' }}>01</span>
      <h3 style={{ fontFamily: 'var(--sn-font-display)', fontWeight: 500, fontSize: 'var(--sn-text-xl)', color: 'var(--sn-heading)', margin: 0 }}>Headline</h3>
      <p style={{ fontFamily: 'var(--sn-font-body)', fontSize: 'var(--sn-type-body-s)', lineHeight: 1.5, color: 'var(--sn-paragraph)', margin: 0, maxWidth: '46ch' }}>
        Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
      </p>
    </>
  );
}

// Contrasting card contexts (re-assert the opposite theme's semantic colors).
const DARK_CTX = {
  background: 'var(--sn-teal-6)',
  '--sn-heading': 'var(--sn-white)',
  '--sn-eyebrow': 'var(--sn-blue-3)',
  '--sn-paragraph': 'var(--sn-grey-4)',
  '--sn-icon': 'var(--sn-teal-3)',
} as CSSProperties;

const LIGHT_CTX = {
  background: 'var(--sn-white)',
  '--sn-heading': 'var(--sn-teal-6)',
  '--sn-eyebrow': 'var(--sn-blue-accent)',
  '--sn-paragraph': 'var(--sn-grey-5)',
  '--sn-icon': 'var(--sn-blue-6)',
} as CSSProperties;

function UsageSlide({ title, contrast }: { title: string; contrast: CSSProperties }) {
  const col: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 'var(--sn-space-4)', justifyContent: 'center' };
  return (
    <div className="slide-body">
      <Header eyebrow="FRAMEWORK · COLOR USAGE" title={title} />
      <div className="sn-work" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sn-space-12)', alignItems: 'stretch' }}>
        <div style={{ ...col, borderLeft: '2px solid var(--sn-stroke)', paddingLeft: 'var(--sn-space-6)' }}>
          <UsageBlock />
        </div>
        <div style={{ ...col, ...contrast, borderRadius: 'var(--sn-radius-xl)', padding: 'var(--sn-space-8)' }}>
          <UsageBlock />
        </div>
      </div>
    </div>
  );
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

const SERIES = [
  { name: 'Series 1', data: [320, 332, 401, 434, 490, 530, 610, 660, 720, 760, 820, 900] },
  { name: 'Series 2', data: [220, 282, 291, 334, 390, 330, 410, 460, 520, 560, 620, 700] },
  { name: 'Series 3', data: [150, 232, 201, 154, 190, 330, 410, 320, 220, 360, 420, 500] },
] as const;

const DCIcon = (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <rect x="3" y="4" width="18" height="6" rx="1.5" />
    <rect x="3" y="14" width="18" height="6" rx="1.5" />
    <circle cx="7" cy="7" r="0.6" fill="currentColor" />
    <circle cx="7" cy="17" r="0.6" fill="currentColor" />
  </svg>
);

const PLACEHOLDER_IMG =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="800" height="1000"><rect width="100%" height="100%" fill="#E9E2DA"/><rect x="40" y="40" width="720" height="920" fill="none" stroke="#BAAFA5" stroke-width="3" stroke-dasharray="12 10"/></svg>',
  );

/* ── General Cards demo (DS Cards › General Cards: Transparent | Card) ── */
const GCARD_GLYPH = (
  <svg width="44" height="44" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <rect x="3" y="3" width="7.5" height="7.5" rx="1.6" />
    <rect x="13.5" y="3" width="7.5" height="7.5" rx="1.6" />
    <rect x="3" y="13.5" width="7.5" height="7.5" rx="1.6" />
    <rect x="13.5" y="13.5" width="7.5" height="7.5" rx="1.6" />
  </svg>
);
const GCARD_BODY =
  'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation.';
const GCARD_NAME: CSSProperties = { fontSize: 'var(--sn-text-xl)' };
const GCARD_DESC: CSSProperties = { fontSize: 'var(--sn-text-base)' };

function GeneralCardsSlide() {
  return (
    <div className="slide-body">
      <Header eyebrow="FRAMEWORK · CARDS" overline="General Cards" title="Transparent · Card" />
      <div className="sn-work" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sn-space-10)', alignItems: 'stretch' }}>
        {/* Transparent — left rule, plain icon, sans eyebrow "01" */}
        <article className="sn-col" style={{ borderLeft: '1px solid var(--sn-stroke)', paddingLeft: 'var(--sn-space-6)', gap: 'var(--sn-space-5)' }}>
          <span className="sn-col-icon" style={{ width: 48, height: 48 }}>{GCARD_GLYPH}</span>
          <span style={{ fontFamily: 'var(--sn-font-body)', fontSize: 'var(--sn-type-body-m)', color: 'var(--sn-eyebrow)' }}>01</span>
          <h3 className="sn-col-name" style={GCARD_NAME}>Headline</h3>
          <p className="sn-col-desc" style={GCARD_DESC}>{GCARD_BODY}</p>
        </article>
        {/* Card — white surface, no border, Blue-01 icon box, no eyebrow */}
        <article className="sn-col sn-col--card sn-col--icon-box" style={{ gap: 'var(--sn-space-5)' }}>
          <span className="sn-col-icon">{GCARD_GLYPH}</span>
          <h3 className="sn-col-name" style={GCARD_NAME}>Headline</h3>
          <p className="sn-col-desc" style={GCARD_DESC}>{GCARD_BODY}</p>
        </article>
      </div>
    </div>
  );
}

/* ── People Cards demo data (DS Cards › People Cards) ── */
const PEOPLE_BODY =
  'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam.';
const PEOPLE_BULLETS = [
  { label: 'Lorem ipsum dolor:', text: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt.' },
  { label: 'Lorem ipsum dolor:', text: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt.' },
] as const;

export const slides: readonly Slide[] = [
  {
    id: 1,
    section: 'Charts',
    theme: 'beige',
    notes: ['Line chart — three series, smooth lines, monthly x-axis.'],
    render: () => (
      <ChartSlide
        eyebrow="FRAMEWORK · CHARTS"
        title="Line chart"
        kind="line"
        categories={MONTHS}
        series={SERIES}
        yName="Active users"
      />
    ),
  },
  {
    id: 2,
    section: 'Charts',
    theme: 'beige',
    notes: ['Area chart — same data, translucent fill.'],
    render: () => (
      <ChartSlide
        eyebrow="FRAMEWORK · CHARTS"
        title="Area chart"
        kind="area"
        categories={MONTHS}
        series={SERIES}
        yName="Active users"
      />
    ),
  },
  {
    id: 3,
    section: 'Charts',
    theme: 'beige',
    notes: ['Stacked bar — three segments per month.'],
    render: () => (
      <ChartSlide
        eyebrow="FRAMEWORK · CHARTS"
        title="Stacked bar"
        kind="bar-stacked"
        categories={MONTHS}
        series={SERIES}
        yName="Active users"
      />
    ),
  },
  {
    id: 4,
    section: 'Tables',
    theme: 'white',
    notes: ['Comparison matrix — SN mark vs customer mark vs text.'],
    render: () => (
      <TableSlide
        eyebrow="FRAMEWORK · TABLES"
        title="Comparison matrix"
        columns={['Hosted Cloud', 'Managed (BYOC)', 'Private Cloud']}
        legend={{ yes: 'StreamNative', no: 'Customer' }}
        rows={[
          { label: 'Cloud Account', cells: ['yes', 'no', 'no'] },
          { label: 'Pulsar Provisioning', cells: ['yes', 'yes', 'no'] },
          { label: 'K8s Provisioning', cells: ['yes', 'yes', 'no'] },
          { label: 'DNS & TLS', cells: ['yes', 'yes', 'no'] },
          { label: 'VPC Peering', cells: ['Coming Soon', 'Available', 'no'] },
        ]}
      />
    ),
  },
  {
    id: 5,
    section: 'Charts',
    theme: 'beige',
    notes: ['Pie (donut) — slice share across protocols.'],
    render: () => (
      <ChartSlide
        eyebrow="FRAMEWORK · CHARTS"
        title="Pie chart"
        kind="pie"
        categories={['Pulsar', 'Kafka', 'MQTT', 'AMQP', 'WebSocket', 'Other']}
        series={[{ name: 'Share', data: [42, 28, 12, 8, 6, 4] }]}
      />
    ),
  },
  {
    id: 6,
    section: 'Charts',
    theme: 'beige',
    notes: ['Radar — three series across seven axes.'],
    render: () => (
      <ChartSlide
        eyebrow="FRAMEWORK · CHARTS"
        title="Radar chart"
        kind="radar"
        max={1000}
        categories={['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']}
        series={[
          { name: 'Series 1', data: [820, 600, 340, 700, 480, 900, 760] },
          { name: 'Series 2', data: [500, 700, 600, 880, 720, 420, 540] },
          { name: 'Series 3', data: [760, 880, 500, 360, 640, 820, 600] },
        ]}
      />
    ),
  },
  {
    id: 7,
    section: 'Charts',
    theme: 'white',
    notes: ['Gauge / progress ring — single percentage with center label.'],
    render: () => (
      <ChartSlide
        eyebrow="FRAMEWORK · CHARTS"
        title="Progress gauge"
        kind="gauge"
        categories={[]}
        series={[]}
        value={68}
        centerLabel="Active users"
      />
    ),
  },
  {
    id: 8,
    section: 'Diagrams',
    theme: 'dark',
    notes: ['Node-and-connector topology — cross data-center replication ring.'],
    render: () => (
      <DiagramSlide
        eyebrow="FRAMEWORK · DIAGRAMS"
        title="Topology diagram"
        nodes={[
          { id: 'dc1', label: 'Data Center 1', icon: DCIcon, x: 27, y: 38 },
          { id: 'dc2', label: 'Data Center 2', icon: DCIcon, x: 73, y: 38 },
          { id: 'dc3', label: 'Data Center 3', icon: DCIcon, x: 50, y: 74 },
        ]}
        connections={[
          { from: 'dc1', to: 'dc2' },
          { from: 'dc2', to: 'dc3' },
          { from: 'dc3', to: 'dc1' },
        ]}
        annotations={[
          { text: 'Replication is done asynchronously', x: 15, y: 58 },
          { text: 'Built-in cross-DC replication, used in production', x: 85, y: 56 },
        ]}
      />
    ),
  },
  {
    id: 9, section: 'Brand', theme: 'dark',
    notes: ['Brand cover — white wordmark + tagline on a dark slide.'],
    render: () => <BrandSlide tagline="Where Pulsar Made Easy" />,
  },
  {
    id: 10, section: 'Layouts', theme: 'white',
    notes: ['Single column with a right-side image panel.'],
    render: () => (
      <SingleColumnSlide
        eyebrow="FRAMEWORK · LAYOUTS"
        title="Single column with image"
        image={{ src: PLACEHOLDER_IMG, alt: 'placeholder' }}
        body={<p>Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.</p>}
      />
    ),
  },
  {
    id: 11, section: 'Layouts', theme: 'white',
    notes: ['Double column with a right-side image panel.'],
    render: () => (
      <DoubleColumnSlide
        eyebrow="FRAMEWORK · LAYOUTS"
        title="Double column with image"
        image={{ src: PLACEHOLDER_IMG, alt: 'placeholder' }}
        left={<p>Lorem ipsum dolor sit amet, consectetur adipiscing elit.</p>}
        right={<p>Sed do eiusmod tempor incididunt ut labore et dolore magna.</p>}
      />
    ),
  },
  {
    id: 12, section: 'Tables', theme: 'white',
    notes: ['Comparison matrix with a highlighted column.'],
    render: () => (
      <TableSlide
        eyebrow="FRAMEWORK · TABLES" title="Highlighted column"
        columns={['Hosted Cloud', 'Managed (BYOC)', 'Private Cloud']}
        legend={{ yes: 'StreamNative', no: 'Customer' }}
        highlightColumn={2}
        rows={[
          { label: 'Cloud Account', cells: ['yes', 'no', 'no'] },
          { label: 'Pulsar Provisioning', cells: ['yes', 'yes', 'no'] },
          { label: 'VPC Peering', cells: ['Coming Soon', 'Available', 'no'] },
        ]}
      />
    ),
  },
  {
    id: 13, section: 'Tables', theme: 'white',
    notes: ['Feature matrix with check / dash marks.'],
    render: () => (
      <TableSlide
        eyebrow="FRAMEWORK · TABLES" title="Feature matrix" mark="check"
        columns={['Free', 'Pro', 'Enterprise']}
        legend={{ yes: 'Included', no: 'Not included' }}
        rows={[
          { label: 'SSO', cells: ['no', 'yes', 'yes'] },
          { label: 'Audit logs', cells: ['no', 'yes', 'yes'] },
          { label: 'SLA', cells: ['no', 'no', 'yes'] },
        ]}
      />
    ),
  },
  {
    id: 14, section: 'People', theme: 'beige',
    notes: ['People text+card — lead text + person cards.'],
    render: () => (
      <PeopleSlide
        eyebrow="FRAMEWORK · PEOPLE" title="Text + card"
        lead={<p>A short lead paragraph that introduces the people featured to the right.</p>}
        people={[
          { name: 'Ada Lovelace', role: 'Engineer' },
          { name: 'Alan Turing', role: 'Researcher' },
        ]}
      />
    ),
  },
  {
    id: 15, section: 'Charts', theme: 'white',
    notes: ['Multi-ring gauge — three concentric progress rings.'],
    render: () => (
      <ChartSlide
        eyebrow="FRAMEWORK · CHARTS" title="Multi-ring gauge"
        kind="gauge" categories={[]} series={[]} centerLabel="Capacity"
        rings={[{ value: 72, label: 'Capacity' }, { value: 54 }, { value: 38 }]}
      />
    ),
  },
  {
    id: 16, section: 'Diagrams', theme: 'dark',
    notes: ['Diagram with elbow connectors + arrowheads.'],
    render: () => (
      <DiagramSlide
        eyebrow="FRAMEWORK · DIAGRAMS" title="Directed flow"
        connectorStyle="elbow" directed
        nodes={[
          { id: 'a', label: 'Producer', x: 22, y: 38 },
          { id: 'b', label: 'Broker', x: 50, y: 70 },
          { id: 'c', label: 'Consumer', x: 78, y: 38 },
        ]}
        connections={[{ from: 'a', to: 'b' }, { from: 'b', to: 'c' }]}
      />
    ),
  },
  {
    id: 17, section: 'CTA', theme: 'dark',
    notes: ['QR event layout — title + QR on the left, image on the right (dark).'],
    render: () => (
      <CtaSlide
        eyebrow="FRAMEWORK · CTA"
        title="Join the workshop"
        url="https://streamnative.io/event"
        qrCaption="Scan to register"
        image={{ src: PLACEHOLDER_IMG, alt: 'placeholder' }}
      />
    ),
  },
  {
    id: 18,
    section: 'Reference',
    theme: 'white',
    notes: ['Type ladder — DS Presentation type roles, rendered live for testing.'],
    render: () => (
      <div className="slide-body">
        <Header eyebrow="FRAMEWORK · TYPE" title="Type scale" />
        <div className="sn-work" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sn-space-4)', justifyContent: 'center' }}>
          {TYPE_ROLES.map((r) => (
            <div key={r.name} style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--sn-space-8)' }}>
              <span style={{ flex: '0 0 120px', fontFamily: 'var(--sn-font-mono)', fontSize: 'var(--sn-type-footnote)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--sn-paragraph)' }}>{r.name}</span>
              <span style={{ fontFamily: r.font, fontSize: r.size, fontWeight: r.weight, lineHeight: r.lh, letterSpacing: r.ls, textTransform: r.upper ? 'uppercase' : 'none', color: 'var(--sn-heading)' }}>{r.sample}</span>
            </div>
          ))}
        </div>
      </div>
    ),
  },
  {
    id: 19,
    section: 'Reference',
    theme: 'white',
    notes: ['Color palette — the six DS families, rendered live for testing.'],
    render: () => (
      <div className="slide-body">
        <Header eyebrow="FRAMEWORK · COLOR" title="Palette" />
        <div className="sn-work" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sn-space-5)', justifyContent: 'center' }}>
          {COLOR_FAMILIES.map((fam) => (
            <div key={fam.name} style={{ display: 'flex', alignItems: 'center', gap: 'var(--sn-space-5)' }}>
              <span style={{ flex: '0 0 64px', fontFamily: 'var(--sn-font-mono)', fontSize: 'var(--sn-type-footnote)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--sn-paragraph)' }}>{fam.name}</span>
              <div style={{ display: 'flex', gap: 'var(--sn-space-2)', flex: 1 }}>
                {fam.steps.map((s) => (
                  <div key={s} style={{ flex: 1 }}>
                    <div style={{ height: 48, borderRadius: 'var(--sn-radius-sm)', background: `var(--sn-${fam.prefix}-${s})`, border: '1px solid var(--sn-stroke)' }} />
                    <span style={{ display: 'block', marginTop: 4, fontFamily: 'var(--sn-font-mono)', fontSize: '10px', color: 'var(--sn-paragraph)', textAlign: 'center' }}>{s}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    ),
  },
  {
    id: 20,
    section: 'Color Usage',
    theme: 'white',
    notes: ['Color usage on a White background — cf. Figma Color Usage Guidelines example 1.'],
    render: () => <UsageSlide title="White background" contrast={DARK_CTX} />,
  },
  {
    id: 21,
    section: 'Color Usage',
    theme: 'beige',
    notes: ['Color usage on a Light Beige background — cf. Figma example 2.'],
    render: () => <UsageSlide title="Light Beige background" contrast={DARK_CTX} />,
  },
  {
    id: 22,
    section: 'Color Usage',
    theme: 'dark',
    notes: ['Color usage on a Dark Teal background — cf. Figma example 3.'],
    render: () => <UsageSlide title="Dark Teal background" contrast={LIGHT_CTX} />,
  },
  {
    id: 23,
    section: 'Reference',
    theme: 'white',
    notes: ['Header — two-tier title (section overline + subsection) + eyebrow + sub.'],
    render: () => (
      <div className="slide-body">
        <Header
          eyebrow="FRAMEWORK · HEADER"
          overline="Section Title"
          title="Subsection Title"
          sub="Optional Funnel Sans Medium subtitle line below the title."
        />
        <div className="sn-work" />
      </div>
    ),
  },
  {
    id: 24,
    section: 'Cards',
    theme: 'white',
    notes: ['General Cards — Transparent (left rule + plain icon + eyebrow) vs Card (white surface, no border, Blue-01 icon box).'],
    render: () => <GeneralCardsSlide />,
  },
  {
    id: 25,
    section: 'Cards',
    theme: 'white',
    notes: ['People Cards · Horizontal feature (L) — square image + big name + bold-labelled bullets.'],
    render: () => (
      <div className="slide-body">
        <Header eyebrow="FRAMEWORK · CARDS" overline="People Cards" title="Horizontal · Feature (L)" />
        <div className="sn-work" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <PeopleCard
            orientation="horizontal" size="L" tone="light"
            name="Name" photo={PLACEHOLDER_IMG} body={PEOPLE_BODY} bullets={PEOPLE_BULLETS}
          />
        </div>
      </div>
    ),
  },
  {
    id: 26,
    section: 'Cards',
    theme: 'white',
    notes: ['People Cards · Horizontal compact (S) — eyebrow + name + body, light and dark tones.'],
    render: () => (
      <div className="slide-body">
        <Header eyebrow="FRAMEWORK · CARDS" overline="People Cards" title="Horizontal · Compact (S)" />
        <div className="sn-work" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sn-space-6)', alignContent: 'center' }}>
          <PeopleCard
            orientation="horizontal" size="S" tone="light"
            name="Name" eyebrow="Lorem ipsum dolor" photo={PLACEHOLDER_IMG} body={PEOPLE_BODY}
          />
          <PeopleCard
            orientation="horizontal" size="S" tone="dark"
            name="Name" eyebrow="Lorem ipsum dolor" photo={PLACEHOLDER_IMG} body={PEOPLE_BODY}
          />
        </div>
      </div>
    ),
  },
  {
    id: 27,
    section: 'Cards',
    theme: 'white',
    notes: ['People Cards · Vertical — image-top cards in L / S sizes, light and dark tones.'],
    render: () => (
      <div className="slide-body">
        <Header eyebrow="FRAMEWORK · CARDS" overline="People Cards" title="Vertical" />
        <div className="sn-work" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 'var(--sn-space-6)', alignItems: 'start' }}>
          <PeopleCard orientation="vertical" size="L" tone="light" name="Name" eyebrow="01" photo={PLACEHOLDER_IMG} body={PEOPLE_BODY} />
          <PeopleCard orientation="vertical" size="S" tone="light" name="Name" eyebrow="01" photo={PLACEHOLDER_IMG} body={PEOPLE_BODY} />
          <PeopleCard orientation="vertical" size="L" tone="dark" name="Name" eyebrow="01" photo={PLACEHOLDER_IMG} body={PEOPLE_BODY} />
          <PeopleCard orientation="vertical" size="S" tone="dark" name="Name" eyebrow="01" photo={PLACEHOLDER_IMG} body={PEOPLE_BODY} />
        </div>
      </div>
    ),
  },
  {
    id: 28,
    section: 'Tables',
    theme: 'beige',
    notes: ['Table — rich header (FREE TRIAL tag + CTA, light theme), text+remark / tag / check cells, left-aligned, highlighted column.'],
    render: () => (
      <TableSlide
        eyebrow="FRAMEWORK · TABLES"
        title="Rich table"
        headerStyle="light"
        align="left"
        mark="check"
        highlightColumn={1}
        columns={[
          'Starter',
          { text: 'Pro', tag: 'Free Trial', cta: 'Choose' },
          { text: 'Enterprise', cta: 'Contact' },
        ]}
        rows={[
          {
            label: 'Throughput',
            cells: [
              { text: '10 MB/s', remark: 'Shared' },
              { text: '100 MB/s', remark: 'Dedicated' },
              { text: 'Custom', remark: 'SLA-backed' },
            ],
          },
          { label: 'Connectors', cells: ['yes', 'yes', 'yes'] },
          { label: 'SSO / SAML', cells: ['no', 'yes', 'yes'] },
          {
            label: 'Support',
            cells: [{ tag: 'Community' }, { text: 'Business hours' }, { text: '24/7', cta: 'Details' }],
          },
        ]}
      />
    ),
  },
];
